import { ReactElement } from 'react'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'

import { CloseIcon, CourseIcon } from 'src/v3/core/icons'

import theme from 'src/theme'
import { Course } from 'src/v3/core/types'

export type CourseData = Course & {
  assignmentData?: {
    completedUsers: string[]
    pendingUsers: string[]
    completedDates: Record<string, string>
  }
}

interface Props {
  entity: CourseData
  onCancel: () => void
}

let UserList = ({
  users,
  label,
  dates,
}: {
  users: string[]
  label: 'Completed' | 'Pending'
  dates?: Record<string, string>
}): ReactElement => (
  <div>
    <Divider />
    <div style={{ padding: 12 }}>
      <div className="font-weight-bold pb-1">{label}</div>
      <div
        style={{
          maxHeight: '25vh',
          overflowWrap: 'break-word',
          overflowY: 'scroll',
        }}
      >
        <ul style={{ marginBottom: 0 }}>
          {(users.length ? users : ['None']).map((u, i) => (
            <li key={`${u}-${i}`}>
              {(label === 'Completed' && dates && u !== 'None') ? `${dates[u]}: ${u}` : u}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
)

let CourseCard = ({ entity, onCancel }: Props): ReactElement => {
  let completionRate = entity.assignmentData
    ? Math.round(
        (100 * entity.assignmentData.completedUsers.length) /
          (entity.assignmentData.completedUsers.length +
            entity.assignmentData.pendingUsers.length),
      )
    : NaN

  return (
    <Card variant="outlined" className="p-3">
      <Box className="flex px-2 mb-2 justify-content-between items-center">
        <div className="flex flex-grow items-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <CourseIcon />
          </Avatar>
          <Typography className="pl-2">{entity.name}</Typography>
        </div>
        <IconButton
          edge="end"
          aria-label="close"
          onClick={onCancel}
          sx={{ marginTop: -3, marginLeft: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {!entity.assignmentData ? (
        <Box
          sx={{
            padding: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <div style={{ padding: 12, overflowWrap: 'break-word' }}>
            <div className="font-weight-bold">Length</div>
            <div>
              {entity.length || 'Unknown'} Hour{entity.length === 1 ? '' : 's'}
            </div>
          </div>
          <Divider />
          <div style={{ padding: 12, overflowWrap: 'break-word' }}>
            <div className="font-weight-bold">Section</div>
            <div>{entity.section || 'Unknown'}</div>
          </div>
        </Box>
      ) : (
        <Box
          sx={{
            padding: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <div style={{ padding: 12, overflowWrap: 'break-word' }}>
            <div className="font-weight-bold">Completion Rate</div>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
              <Box sx={{ width: 'fit-content' }}>
                <Typography variant="body2" color="text.secondary">
                  {completionRate}%
                </Typography>
              </Box>
              <Box sx={{ width: '100%', minWidth: 180 }}>
                <LinearProgress variant="determinate" value={completionRate} />
              </Box>
            </Box>
          </div>
          <UserList
            users={entity.assignmentData.pendingUsers}
            label="Pending"
          />
          <UserList
            users={entity.assignmentData.completedUsers}
            label="Completed"
            dates={entity.assignmentData.completedDates}
          />
        </Box>
      )}
    </Card>
  )
}

export default CourseCard
